import React, { useEffect, useState } from 'react';
import { Navigate, useLocation, useNavigate } from 'react-router-dom';
import { supabase, invokeFunction } from '@/integrations/supabase/client';
import LoadingState from '@/components/LoadingState';
import { useToast } from '@/hooks/use-toast';
import { Button } from '@/components/ui/button';
import { Mail } from 'lucide-react';
import { verifyDeviceMfaCookie } from '@/utils/deviceMfaCookie';
import { otpTypeFromLink } from '@/utils/magic-link/otpType';
import { logLoginActivity } from '@/utils/activityLogger';
import { isMfaRequired } from '@/utils/featureFlags';

function parseAltEmails(raw: unknown): string[] {
  if (Array.isArray(raw)) return (raw as string[]).filter(Boolean);
  if (typeof raw === 'string' && raw) return [raw];
  return [];
}

const AuthCallback = () => {
  const [error, setError] = useState<string | null>(null);
  // Only a token GoTrue refused is an expired link. Everything else failed
  // after sign-in and gets its own message, so a working link is never
  // reported as used.
  const [linkRejected, setLinkRejected] = useState(false);
  const [loading, setLoading] = useState(true);
  const [inputEmail, setInputEmail] = useState('');
  const [sendingLink, setSendingLink] = useState(false);
  const [linkSent, setLinkSent] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const { toast } = useToast();

  const checkCustomMfaEnrollment = async (userId: string): Promise<boolean> => {
    try {
      const { data, error } = await supabase
        .from('user_mfa_factors')
        .select('id')
        .eq('user_id', userId)
        .eq('is_active', true)
        .limit(1);

      if (error) {
        console.error('Error checking MFA enrollment:', error);
        return false;
      }

      return data && data.length > 0;
    } catch (error) {
      console.error('Error checking MFA enrollment:', error);
      return false;
    }
  };

  const checkEmailMfaEnrollment = async (email: string): Promise<boolean> => {
    try {
      // Check session storage first for cached enrollment status
      const cachedEnrollment = sessionStorage.getItem(`figabl_mfa_enrolled_${email}`);
      if (cachedEnrollment === 'true') {
        return true;
      }

      // First try using submission_identifier if available
      const submissionIdentifier = sessionStorage.getItem('figabl_submission_identifier');

      if (submissionIdentifier) {
        const { data: sessionData } = await supabase
          .from('magic_link_sessions')
          .select('email')
          .eq('submission_identifier', submissionIdentifier)
          .maybeSingle();

        if (sessionData && sessionData.email === email) {
          const storedEnrollment = sessionStorage.getItem(`figabl_mfa_enrolled_${email}`);
          return storedEnrollment === 'true';
        }
      }

      // Fallback: Check if there's an active MFA factor for this email via magic link session
      const { data: sessionData } = await supabase
        .from('magic_link_sessions')
        .select('email')
        .eq('email', email)
        .limit(1);

      if (sessionData && sessionData.length > 0) {
        // Check if there are any MFA factors associated with this email
        // Since we can't directly query by email, we'll need to check via the enrollment status
        const storedEnrollment = sessionStorage.getItem(`figabl_mfa_enrolled_${email}`);
        return storedEnrollment === 'true';
      }

      return false;
    } catch (error) {
      console.error('Error checking email MFA enrollment:', error);
      return false;
    }
  };

  const checkForPublishedOrganisation = async (submissionIdentifier: string) => {
    try {
      // First check magic link session
      const { data: sessionData, error: sessionError } = await supabase
        .from('magic_link_sessions')
        .select('email, organisation_id, is_draft')
        .eq('submission_identifier', submissionIdentifier)
        .maybeSingle();

      if (sessionError) {
        console.error('[AuthCallback] Error fetching magic link session:', sessionError);
        return null;
      }

      if (!sessionData) {
        return null;
      }

      // If it's not a draft and has an organisation_id, it's published
      if (!sessionData.is_draft && sessionData.organisation_id) {
        // Get the organisation details
        const { data: orgData, error: orgError } = await supabase
          .from('organisations')
          .select('id, name, poc_email')
          .eq('id', sessionData.organisation_id)
          .maybeSingle();

        if (orgError) {
          console.error('[AuthCallback] Error fetching organisation:', orgError);
          return null;
        }

        return {
          organisation: orgData,
          email: sessionData.email,
          isPublished: true
        };
      }

      return {
        email: sessionData.email,
        isPublished: false
      };
    } catch (error) {
      console.error('[AuthCallback] Error checking for published organisation:', error);
      return null;
    }
  };

  const processAuthCallback = async () => {
    setLoading(true);
    setLinkRejected(false);

    // Marks the token step so its failures read as a link problem and
    // everything after it does not. supabase-js returns an error object, but a
    // transport failure rejects, and both mean the link did not get the caller in.
    const rejectingLink = async <T extends { error: unknown },>(
      step: () => Promise<T>,
    ): Promise<T> => {
      try {
        const result = await step();
        if (result?.error) setLinkRejected(true);
        return result;
      } catch (err) {
        setLinkRejected(true);
        throw err;
      }
    };

    try {
      // Check if there's a hash token in the URL which often contains auth tokens
      const hashParams = new URLSearchParams(window.location.hash.substring(1));
      const accessToken = hashParams.get('access_token');
      const refreshToken = hashParams.get('refresh_token');

      // Get token from query params for magic link flow
      const urlParams = new URLSearchParams(window.location.search);
      const token = urlParams.get('token');

      // Check if we have a specific organization ID to redirect to
      const draftId = urlParams.get('draft_id');

      // Get the submission identifier
      const submissionIdentifier = urlParams.get('submission_identifier');

      // Store submission identifier in sessionStorage for future use
      if (submissionIdentifier) {
        sessionStorage.setItem('figabl_submission_identifier', submissionIdentifier);
      }

      if (token) {
        // Replay the type the link carries. An address with no auth user gets a
        // signup token, and assuming 'magiclink' rejects a first use.
        const { error } = await rejectingLink(() => supabase.auth.verifyOtp({
          token_hash: token,
          type: otpTypeFromLink(urlParams.get('type'))
        }));

        if (error) throw error;

        // Get session after successful verification for logging
        const { data: { session } } = await supabase.auth.getSession();

        // Log the magic link login activity
        if (session?.user?.email) {
          try {
            await logLoginActivity(
              session.user.email,
              'magic_link',
              session.user.id,
              submissionIdentifier
            );
          } catch (logError) {
            // Don't block authentication if activity logging fails
            console.error('[AuthCallback] Failed to log login activity:', logError);
          }
        }

        // Check if this link was sent to an alternative email recipient
        const recipientEmail = urlParams.get('recipient_email');
        if (recipientEmail && session?.user?.id && submissionIdentifier) {
          try {
            const { data: sessionRow } = await supabase
              .from('magic_link_sessions')
              .select('email, alternative_email, organisation_id')
              .eq('submission_identifier', submissionIdentifier)
              .maybeSingle();

            const primaryEmail = sessionRow?.email;
            const altEmails = parseAltEmails(sessionRow?.alternative_email);

            if (
              primaryEmail &&
              recipientEmail !== primaryEmail &&
              altEmails.includes(recipientEmail)
            ) {
              // Look up existing MFA factor for this alt email under this auth user
              const { data: existingFactor } = await supabase
                .from('user_mfa_factors')
                .select('id, phone_number')
                .eq('user_id', session.user.id)
                .eq('secret', recipientEmail)
                .eq('is_active', true)
                .maybeSingle();

              if (isMfaRequired()) {
                if (existingFactor) {
                  navigate('/verify-mfa', {
                    state: {
                      email: recipientEmail,
                      isAltEmailRecipient: true,
                      primaryEmail,
                      organisationId: sessionRow?.organisation_id,
                      from: { pathname: '/step/1' }
                    }
                  });
                  return;
                }
                navigate('/setup-mfa', {
                  state: {
                    email: recipientEmail,
                    isAltEmailEnrollment: true,
                    primaryEmail,
                    organisationId: sessionRow?.organisation_id,
                    from: { pathname: '/step/1' }
                  }
                });
                return;
              }
            }
          } catch (recipientErr) {
            console.error('[AuthCallback] Error handling alt-email recipient flow:', recipientErr);
            // Fall through to normal flow
          }
        }

        // Check if this is for a published organisation
        const orgContext = await checkForPublishedOrganisation(submissionIdentifier);

        if (orgContext?.isPublished && orgContext.organisation) {
          // Log activity with organization context if we don't have session user email
          if (!session?.user?.email && orgContext.email) {
            try {
              await logLoginActivity(
                orgContext.email,
                'magic_link',
                undefined, // No user ID for published org access
                submissionIdentifier
              );
            } catch (logError) {
              console.error('[AuthCallback] Failed to log org login activity:', logError);
            }
          }

          // Check device MFA cookie first
          const deviceMfaResult = await verifyDeviceMfaCookie();

          if (deviceMfaResult.valid && deviceMfaResult.email === orgContext.email) {
            navigate(`/step/1`);
            return;
          }
        } else {
          // Handle draft organization or new user - skip MFA entirely
          if (session?.user) {
            // Draft organization or new user - go directly to form without MFA
            navigate('/step/1');
            return;
          }
        }
      } else if (accessToken && refreshToken) {
        // Handle external oauth flow like Google or GitHub
        const { error } = await rejectingLink(() => supabase.auth.setSession({
          access_token: accessToken,
          refresh_token: refreshToken
        }));

        if (error) throw error;
      }

      // After authentication, redirect to the appropriate page
      if (draftId) {
        // Redirect to edit a specific draft
        navigate(`/step/1?draft_id=${draftId}`);
      } else {
        // Default redirect to the first step of the form
        navigate('/step/1');
      }
    } catch (error) {
      console.error('Authentication error:', error);
      setError(error instanceof Error ? error.message : 'An error occurred during authentication');
      setLoading(false);

      // If auth failed, redirect to login after a delay
      // setTimeout(() => {
      //   navigate('/login');
      // }, 3000);
    }
  };

  useEffect(() => {
    processAuthCallback();
  }, [navigate]);

  useEffect(() => {
    const savedEmail = sessionStorage.getItem('last_auth_email');
    if (savedEmail && !inputEmail) {
      setInputEmail(savedEmail);
    }
  }, [inputEmail]);

  const handleResendMagicLink = async () => {
    try {
      setSendingLink(true);
      setLinkSent(false);  // Reset link sent state when starting the resend

      const urlParams = new URLSearchParams(location.search);
      const submissionIdentifier = urlParams.get('submission_identifier');

      // The server looks up the session and sends the link; it never returns the session's emails.
      const { data, error } = await invokeFunction(supabase, 'get-magic-link-email', {
        body: {
          submissionIdentifier: submissionIdentifier || '',
          preferredEmail: inputEmail || undefined,
        }
      });

      if (error || !data?.success) {
        throw new Error(data?.error || 'Failed to send magic link');
      }

      if (inputEmail) sessionStorage.setItem('last_auth_email', inputEmail);

      setSendingLink(false);
      setLinkSent(true);

      toast({
        title: "Magic link sent",
        description: `Please check your email for the new login link.`
      });
    } catch (err) {
      console.error('Error sending magic link:', err);
      setSendingLink(false);
      toast({
        variant: "destructive",
        title: "Error",
        description: err instanceof Error ? err.message : "Failed to send magic link"
      });
    }
  };

  if (loading) {
    return <LoadingState message="Verifying your authentication..." />;
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-50 flex flex-col">
        <main className="flex-grow flex items-center justify-center p-4">
          <div className="bg-white p-6 rounded-lg shadow-md max-w-md w-full">
            <h2 className="text-red-600 text-xl font-semibold mb-4">
              {linkRejected ? 'Magic Link Expired' : 'Sign-in could not be completed'}
            </h2>
            <p className="text-gray-700 mb-6">
              {linkRejected
                ? 'This link is no longer valid. It may have already been used, or it may have expired. Request a new one below.'
                : error}
            </p>
            <Button
              onClick={handleResendMagicLink}
              className="w-full mb-4 flex items-center justify-center"
              disabled={sendingLink || linkSent}
            >
              <Mail className="mr-2 h-4 w-4" />
              {sendingLink
                ? 'Sending Magic Link...'
                : linkSent
                  ? 'Magic Link Sent'
                  : 'Resend Magic Link'}
            </Button>
          </div>
        </main>
      </div>
    );
  }

  return <Navigate to="/" replace />;
};

export default AuthCallback;
